import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.jsx";
import { Badge } from "@/components/ui/badge.jsx";
import { Button } from "@/components/ui/button.jsx";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs.jsx";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog.jsx";
import { Input } from "@/components/ui/input.jsx";
import {
  AlertTriangle,
  MessageCircle,
  TrendingUp,
  TrendingDown,
  Minus,
  Plus,
  Send,
  X,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
} from "lucide-react";
import { createContext, Fragment, useContext, useState } from "react";
import { useBankTransactions } from "@/lib/hooks/useBanks";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table.jsx";

const riskFactors = [
  {
    facteur: "Trésorerie disponible",
    impact: "positif",
    score: 85,
    details: "Solde moyen supérieur à 15k€",
  },
  {
    facteur: "Défauts de paiement",
    impact: "négatif",
    score: 25,
    details: "3 incidents dans les 6 derniers mois",
  },
  {
    facteur: "Stabilité des revenus",
    impact: "neutre",
    score: 60,
    details: "Variation mensuelle moyenne de 12%",
  },
  {
    facteur: "Ratio d'endettement",
    impact: "négatif",
    score: 40,
    details: "Dette supérieure à 40% des actifs",
  },
  {
    facteur: "Historique client",
    impact: "positif",
    score: 90,
    details: "Client depuis plus de 3 ans",
  },
];

const financements = [
  {
    id: 0,
    financeur: "CIC",
    dateRecu: "avant 2023-06",
    montantEmprunte: "<inconnu>",
    montantRembourse: "280 000€",
    pourcentageRembourse: "<inconnu>",
  },
  {
    id: 1,
    financeur: "DEFACTO",
    dateRecu: "2023-09-25",
    montantEmprunte: "200 000€",
    montantRembourse: "12 000€",
    pourcentageRembourse: "6%",
  },
  {
    id: 2,
    financeur: "SILVR",
    dateRecu: "2023-07-05",
    montantEmprunte: "120 000€",
    montantRembourse: "80 000€",
    pourcentageRembourse: "67%",
  },
];

const RiskInfoContext = createContext(null);

function RiskInfoProvider({ children, company }) {
  const [isChatDialogOpen, setIsChatDialogOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: "amélie",
      text: "Bonjour, je suis Amélie, votre copilote d'analyse financière. Comment puis-je vous aider avec votre analyse de risque aujourd'hui?",
      time: "08:59",
    },
  ]);
  const [currentMessage, setCurrentMessage] = useState("");

  // Sort state for each category
  const [sortBy, setSortBy] = useState({
    "Crédits et remboursements": { field: "date", order: "desc" },
    "Financement poste client": { field: "date", order: "desc" },
    "Location long terme": { field: "date", order: "desc" },
    "REJETS ET IMPAYES": { field: "date", order: "desc" },
    "SAISIES ADMINISTRATIVES": { field: "date", order: "desc" },
  });

  // Get score from API data (support both camelCase and snake_case)
  const score = company?.score ?? company?.score ?? null;
  const score_explanation =
    company?.score_explanation ?? company?.score_explanation ?? null;

  // Only calculate risk level if score exists
  const hasScore = score != null;
  const globalScore = score ?? 0;
  const riskLevel = hasScore
    ? globalScore >= 70
      ? "Faible"
      : globalScore >= 40
        ? "Moyen"
        : "Élevé"
    : null;

  const getImpactIcon = (impact) => {
    switch (impact) {
      case "positif":
        return <TrendingUp className="h-4 w-4 text-green-600" />;
      case "négatif":
        return <TrendingDown className="h-4 w-4 text-red-600" />;
      default:
        return <Minus className="h-4 w-4 text-gray-600" />;
    }
  };

  const getImpactBadge = (impact) => {
    switch (impact) {
      case "positif":
        return (
          <Badge variant="secondary" className="bg-blue-100 text-blue-800">
            positif
          </Badge>
        );
      case "négatif":
        return <Badge variant="destructive">négatif</Badge>;
      default:
        return <Badge variant="outline">neutre</Badge>;
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return "bg-green-500";
    if (score >= 40) return "bg-blue-500";
    return "bg-red-500";
  };

  const getScoreBars = (score) => {
    const bars = [];
    const filledBars = Math.ceil(score / 20);

    for (let i = 0; i < 5; i++) {
      bars.push(
        <div
          key={i}
          className={`w-2 h-8 rounded-sm ${
            i < filledBars ? getScoreColor(score) : "bg-gray-200"
          }`}
        />,
      );
    }
    return bars;
  };

  const handleSendMessage = () => {
    if (currentMessage.trim()) {
      const newMessage = {
        id: messages.length + 1,
        sender: "user",
        text: currentMessage,
        time: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };
      setMessages([...messages, newMessage]);
      setCurrentMessage("");

      // Simulate Amélie response
      setTimeout(() => {
        const response = {
          id: messages.length + 2,
          sender: "amélie",
          text: "Je comprends votre question. Basé sur l'analyse actuelle, votre score de risque de 65 indique un niveau de risque moyen. Les points positifs incluent votre trésorerie disponible et votre historique client.",
          time: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        };
        setMessages((prev) => [...prev, response]);
      }, 1000);
    }
  };

  // Fetch transactions from API
  const companyId = company?.id ? String(company.id) : null;
  const { transactions, isLoading: isLoadingTransactions } =
    useBankTransactions(companyId);

  // Categorize transactions by exact label match (uppercase)
  const categorizeTransactions = () => {
    const categories = {
      "Crédits et remboursements": [],
      "Financement poste client": [],
      "Location long terme": [],
    };

    transactions.forEach((transaction) => {
      const label = (transaction.label || "").toUpperCase();

      // Categorize based on exact label match (uppercase)
      if (label === "CREDITS ET REMBOURSEMENTS") {
        categories["Crédits et remboursements"].push(transaction);
      } else if (label === "FINANCEMENT POSTE CLIENT") {
        categories["Financement poste client"].push(transaction);
      } else if (label === "LOCATION LONG TERME") {
        categories["Location long terme"].push(transaction);
      }
    });

    return categories;
  };

  const categorizedTransactions = categorizeTransactions();

  // Categorize alert transactions (REJETS ET IMPAYES and SAISIES ADMINISTRATIVES)
  const categorizeAlertTransactions = () => {
    const categories = {
      "REJETS ET IMPAYES": [],
      "SAISIES ADMINISTRATIVES": [],
    };

    transactions.forEach((transaction) => {
      const description = (transaction.description || "").toLowerCase();
      const label = (transaction.label || "").toUpperCase();

      // Check if label is exactly "SAISIES ADMINISTRATIVES"
      if (label === "SAISIES ADMINISTRATIVES") {
        categories["SAISIES ADMINISTRATIVES"].push(transaction);
        return;
      }

      // Exclude commissions and penalties from all categories
      const excludeKeywords = [
        "commission",
        "commissions",
        "pénalité",
        "pénalités",
        "penalite",
        "penalites",
      ];
      const isExcluded = excludeKeywords.some((keyword) =>
        description.includes(keyword),
      );

      if (isExcluded) {
        return; // Skip this transaction - don't categorize it
      }

      // Keywords for rejections and unpaid items (combined)
      const rejectionUnpaidKeywords = [
        "rejet",
        "rejeté",
        "refus",
        "refusé",
        "insuffisant",
        "solde insuffisant",
        "opposition",
        "impayé",
        "impayés",
        "non payé",
        "non payée",
        "défaut",
        "défaillance",
        "non honoré",
      ];

      const isRejectionOrUnpaid = rejectionUnpaidKeywords.some((keyword) =>
        description.includes(keyword),
      );

      if (isRejectionOrUnpaid) {
        categories["REJETS ET IMPAYES"].push(transaction);
      }
    });

    return categories;
  };

  const categorizedAlertTransactions = categorizeAlertTransactions();

  // Format date helper
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("fr-FR");
  };

  // Format amount helper
  const formatAmount = (amount) => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return amount;
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(numAmount);
  };

  // Sort transactions helper
  const sortTransactions = (transactions, category) => {
    const sortConfig = sortBy[category] || { field: "date", order: "desc" };
    const sorted = [...transactions].sort((a, b) => {
      if (sortConfig.field === "date") {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        return sortConfig.order === "asc" ? dateA - dateB : dateB - dateA;
      } else {
        const valueA = parseFloat(a.amount) || 0;
        const valueB = parseFloat(b.amount) || 0;
        return sortConfig.order === "asc" ? valueA - valueB : valueB - valueA;
      }
    });
    return sorted;
  };

  const handleSortChange = (category, field, order) => {
    setSortBy((prev) => ({
      ...prev,
      [category]: { field, order },
    }));
  };

  const value = {
    handleSortChange,
    handleSendMessage,
    sortTransactions,
    formatAmount,
    formatDate,
    categorizeAlertTransactions,
    categorizedAlertTransactions,
    categorizeTransactions,
    categorizedTransactions,
    getImpactBadge,
    getImpactIcon,
    getScoreBars,
    getScoreColor,
    isChatDialogOpen,
    setIsChatDialogOpen,
    riskLevel,
    globalScore,
    hasScore,
    messages,
    currentMessage,
    isLoadingTransactions,
    score,
    score_explanation,
    sortBy
  };

  return (
    <RiskInfoContext.Provider value={value}>
      {children}
    </RiskInfoContext.Provider>
  );
}

function useRiskInfo() {
  return useContext(RiskInfoContext);
}

function RiskInfoComponent() {
  return (
    <div className="p-6 space-y-6">
      <RiskInfoHeader />

      {/* <RiskInfoCopilotDialog /> */}

      <Tabs defaultValue="score" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="score">Score Risque</TabsTrigger>
          <TabsTrigger value="financements">Financements détectés</TabsTrigger>
          <TabsTrigger value="alertes">Cash Alertes</TabsTrigger>
        </TabsList>

        <TabsContent value="score" className="space-y-6">
          <RiskInfoScore />
        </TabsContent>

        <TabsContent value="financements" className="space-y-6">
          <RiskInfoFinancements />
        </TabsContent>

        <TabsContent value="alertes" className="space-y-6">
          <RiskInfoAlerts />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default function RiskInfo({ company }) {
  return (
    <RiskInfoProvider company={company}>
      <RiskInfoComponent />
    </RiskInfoProvider>
  );
}

function RiskInfoHeader() {
  return (
    <div>
      <h1 className="text-3xl font-bold tracking-tight">Information risque</h1>
      <p className="text-muted-foreground mt-2">
        Analyse du risque financier et opérationnel
      </p>
    </div>
  );
}

function RiskInfoCopilotDialog() {
  const {
    handleSendMessage,
    isChatDialogOpen,
    setIsChatDialogOpen,
    messages,
    currentMessage,
  } = useRiskInfo();

  // Copilote Amélie - Compact version
  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
      <Dialog open={isChatDialogOpen} onOpenChange={setIsChatDialogOpen}>
        <DialogTrigger asChild>
          <div className="p-4 cursor-pointer hover:bg-blue-100/50 dark:hover:bg-blue-900/20 transition-colors rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                  <MessageCircle className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-blue-900 dark:text-blue-100">
                    Copilote Amélie
                  </h3>
                  <p className="text-xs text-blue-600 dark:text-blue-300">
                    Posez vos questions sur le risque financier
                  </p>
                </div>
              </div>
            </div>
          </div>
        </DialogTrigger>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              Chat avec Copilote Amélie
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col h-[500px]">
            <div className="flex-1 overflow-y-auto space-y-4 p-4 border rounded-lg">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.sender === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                      message.sender === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted"
                    }`}
                  >
                    <p className="text-sm">{message.text}</p>
                    <div className="text-xs opacity-70 mt-1">
                      {message.time}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <Input
                placeholder="Posez votre question sur le risque financier..."
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
              />
              <Button onClick={handleSendMessage}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RiskInfoScore() {
  const {
    riskLevel,
    globalScore,
    hasScore,
    score_explanation,
  } = useRiskInfo();

  const labelMapping = {
    net_cash_3m: "Net cash flow (inflows minus outflows) over the 6-month observation window",
    total_in_3m: "Total positive cash inflows",
    total_out_3m: "Total negative cash outflows",
    total_abs_3m: "Total transaction volume (absolute values)",
    tx_count_3m: "Number of transactions",
    avg_tx_value_3m: "Average transaction amount",
    std_tx_value_3m: "Volatility of transaction amounts",
    active_days_3m: "Number of distinct days with at least one transaction",
    default_tx_count_3m: "Number of rejected/unpaid transactions",
    net_cash_last_half: "Net cash flow in the most recent 3 months",
    net_cash_first_half: "Net cash flow in the earliest 3 months",
    default_tx_count_last_half: "Rejected/unpaid count in the most recent 3 months",
    default_tx_count_first_half: "Rejected/unpaid count in the earliest 3 months",
    label_nunique_3m: "Number of distinct transaction categories observed",
    sum_operational_3m: "Total amount in operational categories (purchases, HR, working capital)",
    sum_stress_3m: "Total amount in financial stress categories (rejections, bank fees, penalties, seizures)",
    sum_stress_last_half: "Financial stress amount in the most recent 3 months",
    sum_stress_first_half: "Financial stress amount in the earliest 3 months",
    count_rejets_et_impayes_last_half: "Rejection/unpaid count in the most recent 3 months",
    count_rejets_et_impayes_first_half: "Rejection/unpaid count in the earliest 3 months",
    count_frais_bancaires_last_half: "Bank fee count in the most recent 3 months",
    count_frais_bancaires_first_half: "Bank fee count in the earliest 3 months",
    net_cash_trend_3m: "Change in net cash flow between the two 3-month halves (positive = improving)",
    default_tx_trend_3m: "Change in rejection count between the two 3-month halves (positive = worsening)",
    avg_tx_per_active_day_3m: "Average number of transactions per active day",
    stress_share_3m: "Financial stress as a share of total transaction volume",
    operational_share_3m: "Operational activity as a share of total transaction volume",
    stress_trend_3m: "Change in financial stress amount between the two 3-month halves",
  };

  const getLabel = (key) => {
    if (labelMapping[key]) return labelMapping[key];
    if (key.startsWith("sum_")) return `Total amount in ${key.split("_")[1]} category`;
    if (key.startsWith("count_")) return `Number of transactions in ${key.split("_")[1]} category`;
    return key;
  };

  return (
    <Fragment>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" /> Score de risque global
          </CardTitle>
        </CardHeader>
        <CardContent>
          {hasScore ? (
            <div className="text-center space-y-2">
              <div className="text-6xl font-bold">{globalScore}/100</div>
              {riskLevel && (
                <div className="text-lg font-medium">Risque {riskLevel}</div>
              )}
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              Score non disponible
            </div>
          )}
        </CardContent>
      </Card>

      {score_explanation && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Détails de l'analyse</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50%]">Indicateur</TableHead>
                    <TableHead className="text-right">Valeur</TableHead>
                    <TableHead className="text-right">Impact Score</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(score_explanation).map(([key, data]) => (
                    <TableRow key={key}>
                      <TableCell className="font-medium text-sm">
                        {getLabel(key)}
                      </TableCell>
                      <TableCell className="text-right">
                        {typeof data.value === "number" 
                          ? data.value.toLocaleString(undefined, { maximumFractionDigits: 2 }) 
                          : data.value}
                      </TableCell>
                      <TableCell 
                        className={`text-right font-bold ${
                          data.contribution > 0 ? "text-green-600" : data.contribution < 0 ? "text-red-600" : ""
                        }`}
                      >
                        {data.contribution > 0 ? `+${data.contribution}` : data.contribution}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </Fragment>
  );
}

function RiskInfoFinancements() {
  const {
    handleSortChange,
    sortTransactions,
    formatAmount,
    formatDate,
    categorizedTransactions,
    isLoadingTransactions,
    sortBy
  } = useRiskInfo();

  return (
    <Fragment>
      {isLoadingTransactions ? (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              <p className="text-muted-foreground">
                Chargement des transactions...
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="max-h-[800px] overflow-y-auto space-y-6 pr-2">
          {/* Crédits et remboursements */}
          <Card>
            <CardHeader>
              <CardTitle>Crédits et remboursements</CardTitle>
            </CardHeader>
            <CardContent>
              {categorizedTransactions["Crédits et remboursements"].length ===
              0 ? (
                <p className="text-sm text-muted-foreground">
                  Aucune transaction dans cette catégorie
                </p>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-4 gap-4 text-sm font-medium text-muted-foreground border-b pb-2">
                    <div
                      className="flex items-center gap-1 cursor-pointer hover:text-foreground"
                      onClick={() => {
                        const current = sortBy["Crédits et remboursements"];
                        if (current.field === "date") {
                          handleSortChange(
                            "Crédits et remboursements",
                            "date",
                            current.order === "asc" ? "desc" : "asc",
                          );
                        } else {
                          handleSortChange(
                            "Crédits et remboursements",
                            "date",
                            "desc",
                          );
                        }
                      }}
                    >
                      Date
                      {sortBy["Crédits et remboursements"].field === "date" ? (
                        sortBy["Crédits et remboursements"].order === "asc" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-50" />
                      )}
                    </div>
                    <div>Description</div>
                    <div
                      className="flex items-center gap-1 cursor-pointer hover:text-foreground"
                      onClick={() => {
                        const current = sortBy["Crédits et remboursements"];
                        if (current.field === "value") {
                          handleSortChange(
                            "Crédits et remboursements",
                            "value",
                            current.order === "asc" ? "desc" : "asc",
                          );
                        } else {
                          handleSortChange(
                            "Crédits et remboursements",
                            "value",
                            "desc",
                          );
                        }
                      }}
                    >
                      Montant
                      {sortBy["Crédits et remboursements"].field === "value" ? (
                        sortBy["Crédits et remboursements"].order === "asc" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-50" />
                      )}
                    </div>
                    <div>Label</div>
                  </div>
                  <div className="max-h-[400px] overflow-y-auto">
                    {sortTransactions(
                      categorizedTransactions["Crédits et remboursements"],
                      "Crédits et remboursements",
                    ).map((transaction) => (
                      <div
                        key={transaction.id}
                        className="grid grid-cols-4 gap-4 items-center py-3 border-b last:border-b-0"
                      >
                        <div className="text-sm">
                          {formatDate(transaction.date)}
                        </div>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="text-sm line-clamp-2 cursor-help">
                                {transaction.description || "—"}
                              </div>
                            </TooltipTrigger>
                            {transaction.description && (
                              <TooltipContent>
                                <p className="max-w-xs">
                                  {transaction.description}
                                </p>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                        <div className="text-sm font-medium">
                          {formatAmount(transaction.amount)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {transaction.label || "—"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Financement poste client */}
          <Card>
            <CardHeader>
              <CardTitle>Financement poste client</CardTitle>
            </CardHeader>
            <CardContent>
              {categorizedTransactions["Financement poste client"].length ===
              0 ? (
                <p className="text-sm text-muted-foreground">
                  Aucune transaction dans cette catégorie
                </p>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-4 gap-4 text-sm font-medium text-muted-foreground border-b pb-2">
                    <div
                      className="flex items-center gap-1 cursor-pointer hover:text-foreground"
                      onClick={() => {
                        const current = sortBy["Financement poste client"];
                        if (current.field === "date") {
                          handleSortChange(
                            "Financement poste client",
                            "date",
                            current.order === "asc" ? "desc" : "asc",
                          );
                        } else {
                          handleSortChange(
                            "Financement poste client",
                            "date",
                            "desc",
                          );
                        }
                      }}
                    >
                      Date
                      {sortBy["Financement poste client"].field === "date" ? (
                        sortBy["Financement poste client"].order === "asc" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-50" />
                      )}
                    </div>
                    <div>Description</div>
                    <div
                      className="flex items-center gap-1 cursor-pointer hover:text-foreground"
                      onClick={() => {
                        const current = sortBy["Financement poste client"];
                        if (current.field === "value") {
                          handleSortChange(
                            "Financement poste client",
                            "value",
                            current.order === "asc" ? "desc" : "asc",
                          );
                        } else {
                          handleSortChange(
                            "Financement poste client",
                            "value",
                            "desc",
                          );
                        }
                      }}
                    >
                      Montant
                      {sortBy["Financement poste client"].field === "value" ? (
                        sortBy["Financement poste client"].order === "asc" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-50" />
                      )}
                    </div>
                    <div>Label</div>
                  </div>
                  <div className="max-h-[400px] overflow-y-auto">
                    {sortTransactions(
                      categorizedTransactions["Financement poste client"],
                      "Financement poste client",
                    ).map((transaction) => (
                      <div
                        key={transaction.id}
                        className="grid grid-cols-4 gap-4 items-center py-3 border-b last:border-b-0"
                      >
                        <div className="text-sm">
                          {formatDate(transaction.date)}
                        </div>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="text-sm line-clamp-2 cursor-help">
                                {transaction.description || "—"}
                              </div>
                            </TooltipTrigger>
                            {transaction.description && (
                              <TooltipContent>
                                <p className="max-w-xs">
                                  {transaction.description}
                                </p>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                        <div className="text-sm font-medium">
                          {formatAmount(transaction.amount)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {transaction.label || "—"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Location long terme */}
          <Card>
            <CardHeader>
              <CardTitle>Location long terme</CardTitle>
            </CardHeader>
            <CardContent>
              {categorizedTransactions["Location long terme"].length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Aucune transaction dans cette catégorie
                </p>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-4 gap-4 text-sm font-medium text-muted-foreground border-b pb-2">
                    <div
                      className="flex items-center gap-1 cursor-pointer hover:text-foreground"
                      onClick={() => {
                        const current = sortBy["Location long terme"];
                        if (current.field === "date") {
                          handleSortChange(
                            "Location long terme",
                            "date",
                            current.order === "asc" ? "desc" : "asc",
                          );
                        } else {
                          handleSortChange(
                            "Location long terme",
                            "date",
                            "desc",
                          );
                        }
                      }}
                    >
                      Date
                      {sortBy["Location long terme"].field === "date" ? (
                        sortBy["Location long terme"].order === "asc" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-50" />
                      )}
                    </div>
                    <div>Description</div>
                    <div
                      className="flex items-center gap-1 cursor-pointer hover:text-foreground"
                      onClick={() => {
                        const current = sortBy["Location long terme"];
                        if (current.field === "value") {
                          handleSortChange(
                            "Location long terme",
                            "value",
                            current.order === "asc" ? "desc" : "asc",
                          );
                        } else {
                          handleSortChange(
                            "Location long terme",
                            "value",
                            "desc",
                          );
                        }
                      }}
                    >
                      Montant
                      {sortBy["Location long terme"].field === "value" ? (
                        sortBy["Location long terme"].order === "asc" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-50" />
                      )}
                    </div>
                    <div>Label</div>
                  </div>
                  <div className="max-h-[400px] overflow-y-auto">
                    {sortTransactions(
                      categorizedTransactions["Location long terme"],
                      "Location long terme",
                    ).map((transaction) => (
                      <div
                        key={transaction.id}
                        className="grid grid-cols-4 gap-4 items-center py-3 border-b last:border-b-0"
                      >
                        <div className="text-sm">
                          {formatDate(transaction.date)}
                        </div>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="text-sm line-clamp-2 cursor-help">
                                {transaction.description || "—"}
                              </div>
                            </TooltipTrigger>
                            {transaction.description && (
                              <TooltipContent>
                                <p className="max-w-xs">
                                  {transaction.description}
                                </p>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                        <div className="text-sm font-medium">
                          {formatAmount(transaction.amount)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {transaction.label || "—"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </Fragment>
  );
}

function RiskInfoAlerts() {
  const {
    handleSortChange,
    sortTransactions,
    formatAmount,
    formatDate,
    categorizedAlertTransactions,
    isLoadingTransactions,
    sortBy
  } = useRiskInfo();

  return (
    <Fragment>
      {isLoadingTransactions ? (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              <p className="text-muted-foreground">Chargement des alertes...</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* REJETS ET IMPAYES */}
          <Card>
            <CardHeader>
              <CardTitle>REJETS ET IMPAYES</CardTitle>
            </CardHeader>
            <CardContent>
              {categorizedAlertTransactions["REJETS ET IMPAYES"].length ===
              0 ? (
                <p className="text-sm text-muted-foreground">
                  Aucune transaction dans cette catégorie
                </p>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-4 gap-4 text-sm font-medium text-muted-foreground border-b pb-2">
                    <div
                      className="flex items-center gap-1 cursor-pointer hover:text-foreground"
                      onClick={() => {
                        const current = sortBy["REJETS ET IMPAYES"];
                        if (current.field === "date") {
                          handleSortChange(
                            "REJETS ET IMPAYES",
                            "date",
                            current.order === "asc" ? "desc" : "asc",
                          );
                        } else {
                          handleSortChange("REJETS ET IMPAYES", "date", "desc");
                        }
                      }}
                    >
                      Date
                      {sortBy["REJETS ET IMPAYES"].field === "date" ? (
                        sortBy["REJETS ET IMPAYES"].order === "asc" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-50" />
                      )}
                    </div>
                    <div>Description</div>
                    <div
                      className="flex items-center gap-1 cursor-pointer hover:text-foreground"
                      onClick={() => {
                        const current = sortBy["REJETS ET IMPAYES"];
                        if (current.field === "value") {
                          handleSortChange(
                            "REJETS ET IMPAYES",
                            "value",
                            current.order === "asc" ? "desc" : "asc",
                          );
                        } else {
                          handleSortChange(
                            "REJETS ET IMPAYES",
                            "value",
                            "desc",
                          );
                        }
                      }}
                    >
                      Montant
                      {sortBy["REJETS ET IMPAYES"].field === "value" ? (
                        sortBy["REJETS ET IMPAYES"].order === "asc" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-50" />
                      )}
                    </div>
                    <div>Label</div>
                  </div>
                  <div className="max-h-[400px] overflow-y-auto">
                    {sortTransactions(
                      categorizedAlertTransactions["REJETS ET IMPAYES"],
                      "REJETS ET IMPAYES",
                    ).map((transaction) => (
                      <div
                        key={transaction.id}
                        className="grid grid-cols-4 gap-4 items-center py-3 border-b last:border-b-0"
                      >
                        <div className="text-sm">
                          {formatDate(transaction.date)}
                        </div>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="text-sm line-clamp-2 cursor-help">
                                {transaction.description || "—"}
                              </div>
                            </TooltipTrigger>
                            {transaction.description && (
                              <TooltipContent>
                                <p className="max-w-xs">
                                  {transaction.description}
                                </p>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                        <div className="text-sm font-medium">
                          {formatAmount(transaction.amount)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {transaction.label || "—"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* SAISIES ADMINISTRATIVES */}
          <Card>
            <CardHeader>
              <CardTitle>SAISIES ADMINISTRATIVES</CardTitle>
            </CardHeader>
            <CardContent>
              {categorizedAlertTransactions["SAISIES ADMINISTRATIVES"]
                .length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Aucune transaction dans cette catégorie
                </p>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-4 gap-4 text-sm font-medium text-muted-foreground border-b pb-2">
                    <div
                      className="flex items-center gap-1 cursor-pointer hover:text-foreground"
                      onClick={() => {
                        const current = sortBy["SAISIES ADMINISTRATIVES"];
                        if (current.field === "date") {
                          handleSortChange(
                            "SAISIES ADMINISTRATIVES",
                            "date",
                            current.order === "asc" ? "desc" : "asc",
                          );
                        } else {
                          handleSortChange(
                            "SAISIES ADMINISTRATIVES",
                            "date",
                            "desc",
                          );
                        }
                      }}
                    >
                      Date
                      {sortBy["SAISIES ADMINISTRATIVES"].field === "date" ? (
                        sortBy["SAISIES ADMINISTRATIVES"].order === "asc" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-50" />
                      )}
                    </div>
                    <div>Description</div>
                    <div
                      className="flex items-center gap-1 cursor-pointer hover:text-foreground"
                      onClick={() => {
                        const current = sortBy["SAISIES ADMINISTRATIVES"];
                        if (current.field === "value") {
                          handleSortChange(
                            "SAISIES ADMINISTRATIVES",
                            "value",
                            current.order === "asc" ? "desc" : "asc",
                          );
                        } else {
                          handleSortChange(
                            "SAISIES ADMINISTRATIVES",
                            "value",
                            "desc",
                          );
                        }
                      }}
                    >
                      Montant
                      {sortBy["SAISIES ADMINISTRATIVES"].field === "value" ? (
                        sortBy["SAISIES ADMINISTRATIVES"].order === "asc" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-50" />
                      )}
                    </div>
                    <div>Label</div>
                  </div>
                  <div className="max-h-[400px] overflow-y-auto">
                    {sortTransactions(
                      categorizedAlertTransactions["SAISIES ADMINISTRATIVES"],
                      "SAISIES ADMINISTRATIVES",
                    ).map((transaction) => (
                      <div
                        key={transaction.id}
                        className="grid grid-cols-4 gap-4 items-center py-3 border-b last:border-b-0"
                      >
                        <div className="text-sm">
                          {formatDate(transaction.date)}
                        </div>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="text-sm line-clamp-2 cursor-help">
                                {transaction.description || "—"}
                              </div>
                            </TooltipTrigger>
                            {transaction.description && (
                              <TooltipContent>
                                <p className="max-w-xs">
                                  {transaction.description}
                                </p>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                        <div className="text-sm font-medium">
                          {formatAmount(transaction.amount)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {transaction.label || "—"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Fragment>
  );
}
